import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import "./LoginPage.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import base_url from "../../api";

const LoginPage = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // ✅ State for logged-in user
  const [loggedInUser, setLoggedInUser] = useState(null);

  // ✅ Change Password states
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [updating, setUpdating] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({});

  useEffect(() => {
    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");

    if (token && user) {
      try {
        setLoggedInUser(JSON.parse(user));
      } catch {
        localStorage.removeItem("user");
      }
    }
  }, []);

  const validateEmail = (email) => {
    if (!email.trim()) {
      return "Email is required";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      return "Please enter a valid email address";
    }
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const emailError = validateEmail(email);
    if (emailError) {
      setError(emailError);
      setLoading(false);
      return;
    }

    if (!password.trim()) {
      setError("Password is required");
      setLoading(false);
      return;
    }

    setError("");
    setLoading(true);

    try {
      const response = await fetch(`${base_url}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok && data.token) {
        localStorage.setItem("token", data.token);
        localStorage.setItem("user", JSON.stringify(data.user));
        setLoggedInUser(data.user);

        toast.success(data.message || "Login successful!", {
          position: "top-right",
          autoClose: 2000,
        });

        setTimeout(() => {
          navigate("/");
        }, 2000);
      } else {
        // Check if email is not verified
        if (response.status === 403 && !data.emailVerified) {
          setError(data.message || "Please verify your email address before logging in.");
          toast.error(data.message || "Please verify your email address. Check your inbox for the verification link.", {
            position: "top-right",
            autoClose: 5000,
          });
        } else {
          setError(data.message || "Invalid credentials. Please try again.");
        }
      }
    } catch (err) {
      setError("Something went wrong. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  function register() {
    navigate("/register");
  }

  function logout() {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setLoggedInUser(null);
  }

  // ✅ Handle password update
  const handlePasswordUpdate = async (e) => {
    e.preventDefault();

    // Validate passwords and set errors
    const newErrors = {};
    let hasErrors = false;

    if (!currentPassword.trim()) {
      newErrors.currentPassword = "Current password is required.";
      hasErrors = true;
    }

    if (!newPassword.trim()) {
      newErrors.newPassword = "New password is required.";
      hasErrors = true;
    } else if (newPassword.length < 6) {
      newErrors.newPassword = "New password must be at least 6 characters long.";
      hasErrors = true;
    } else if (newPassword.length > 50) {
      newErrors.newPassword = "New password must be less than 50 characters.";
      hasErrors = true;
    } else if (newPassword === currentPassword) {
      newErrors.newPassword = "New password must be different from current password.";
      hasErrors = true;
    }

    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = "Please confirm your new password.";
      hasErrors = true;
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "New password and confirm password do not match.";
      hasErrors = true;
    }

    if (hasErrors) {
      setPasswordErrors(newErrors);
      toast.error("Please fix the errors in the form", { position: "top-right" });
      return;
    }

    setPasswordErrors({});

    setUpdating(true);
    try {
      const token = localStorage.getItem("token");
      const user = JSON.parse(localStorage.getItem("user")); // ✅ Parse stored user

      if (!user?.email) {
        toast.error("User not found in local storage.");
        setUpdating(false);
        return;
      }

      // ✅ Make API call
      const response = await fetch(`${base_url}/update_password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: user.email,          
          old_pass: currentPassword,  
          new_pass: newPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message || "Password updated successfully!");
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setPasswordErrors({});
        localStorage.removeItem("token");
        localStorage.removeItem("user");
      } else {
        toast.error(data.error || data.message || "Failed to update password.");
      }
    } catch (err) {
      console.error("Error:", err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setUpdating(false);
    }
  };



  return (
    <div className="login-page">
      {loggedInUser ? (
        // ✅ Already Logged In: Show User Info + Change Password
        <div className="user-info">
          <div className="user-left">
            <h2>Welcome, {loggedInUser.name} 👋</h2>
            <div>
              <p><strong>Email:</strong> {loggedInUser.email}</p>
              <p><strong>Phone:</strong> {loggedInUser.phone}</p>
            </div>
            <button className="btn" onClick={logout}>Logout</button>
          </div>
          {/* Change Password Form */}
          <div className="change-password login-form">
            <h3>Change Password</h3>

            <form className="login-form" onSubmit={handlePasswordUpdate}>
              <div className="form-group">
                <input
                  type="password"
                  placeholder="Current Password*"
                  value={currentPassword}
                  onChange={(e) => {
                    setCurrentPassword(e.target.value);
                    if (passwordErrors.currentPassword) {
                      setPasswordErrors({ ...passwordErrors, currentPassword: "" });
                    }
                  }}
                  className={passwordErrors.currentPassword ? "error-input" : ""}
                  required
                />
                {passwordErrors.currentPassword && (
                  <span className="error-message">{passwordErrors.currentPassword}</span>
                )}
              </div>

              <div className="form-group">
                <input
                  type="password"
                  placeholder="New Password*"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    if (passwordErrors.newPassword) {
                      setPasswordErrors({ ...passwordErrors, newPassword: "" });
                    }
                    if (passwordErrors.confirmPassword && e.target.value === confirmPassword) {
                      setPasswordErrors({ ...passwordErrors, confirmPassword: "" });
                    }
                  }}
                  className={passwordErrors.newPassword ? "error-input" : ""}
                  required
                />
                {passwordErrors.newPassword && (
                  <span className="error-message">{passwordErrors.newPassword}</span>
                )}
              </div>

              <div className="form-group">
                <input
                  type="password"
                  placeholder="Confirm New Password*"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (passwordErrors.confirmPassword) {
                      setPasswordErrors({ ...passwordErrors, confirmPassword: "" });
                    }
                    if (passwordErrors.confirmPassword && e.target.value === newPassword) {
                      setPasswordErrors({ ...passwordErrors, confirmPassword: "" });
                    }
                  }}
                  className={passwordErrors.confirmPassword ? "error-input" : ""}
                  required
                />
                {passwordErrors.confirmPassword && (
                  <span className="error-message">{passwordErrors.confirmPassword}</span>
                )}
              </div>

              <button type="submit" className="btn" disabled={updating}>
                {updating ? "Updating..." : "Update Password"}
              </button>
            </form>
          </div>

        </div>
      ) : (
        <>
          {/* Login Section */}
          <div className="login-left">
            <h2>Login</h2>
            <form className="login-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <input
                  type="email"
                  placeholder="Email Address*"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="form-group password-group">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password*"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <span
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>

              {error && <p className="error-message">{error}</p>}

              <div className="form-options">
                <Link to="/forgot-password">Forgot Your Password?</Link>
              </div>

              <button type="submit" className="btn" disabled={loading}>
                {loading ? "Logging in..." : "Login"}
              </button>
            </form>
          </div>

          {/* New Customer Section */}
          <div className="login-right">
            <h2>New Customer</h2>
            <p>
              Be part of our growing family of new customers! Join us today and
              unlock a world of exclusive benefits, offers, and personalized
              experiences.
            </p>
            <button onClick={register} className="btn">
              Register
            </button>
          </div>
        </>
      )}

      {/* ✅ Toast container must be here */}
      <ToastContainer />
    </div>
  );
};

export default LoginPage;
